import { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Badge } from "react-bootstrap";
import { updateAccountCash } from "../../../service/api/accountService";
import { toast } from "sonner";
import { useLoader } from "../../../service/Hooks/useLoader";
import { VALIDATION_STATUS } from "../../../service/utils/validationStatus";

/**
 * COMPOSANT CashEditForm - Formulaire d'ajout de cash avec preuve
 * @param {Object} props - Propriétés du composant
 * @param {boolean} props.show - Contrôle l'affichage du modal
 * @param {Function} props.onClose - Fonction de fermeture du modal
 * @param {Object} props.account - Compte à modifier
 * @param {Function} props.onSuccess - Callback après succès
 */
const CashEditForm = ({ show, onClose, account, onSuccess }) => {
  // ÉTATS DU FORMULAIRE
  const [amount, setAmount] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState("");
  const [touched, setTouched] = useState(false);
  const [isAccountValidated, setIsAccountValidated] = useState(true); // NOUVEL ÉTAT
  const [validationError, setValidationError] = useState(""); // NOUVEL ÉTAT
  const { showLoader, hideLoader } = useLoader();

  // Vérification de l'account
  if (!account || !account.id) return null;

  /**
   * VÉRIFICATION DU STATUT DE VALIDATION DU COMPTE
   */
  useEffect(() => {
    if (account && show) {
      const validated = account.validationStatus === VALIDATION_STATUS.VALIDATED;
      setIsAccountValidated(validated);
      
      if (!validated) {
        const statusText = getValidationStatusText(account.validationStatus);
        setValidationError(`Ce compte n'est pas validé. Statut actuel : ${statusText}.`);
      } else {
        setValidationError("");
      }
    }
  }, [account, show]);

  /**
   * Obtient le texte du statut de validation
   */
  const getValidationStatusText = (status) => {
    switch (status) {
      case VALIDATION_STATUS.DRAFT:
        return "Brouillon";
      case VALIDATION_STATUS.PENDING:
        return "En attente de validation";
      case VALIDATION_STATUS.VALIDATED:
        return "Validé";
      case VALIDATION_STATUS.REJECTED:
        return "Rejeté";
      default:
        return "Inconnu";
    }
  };

  /**
   * Obtient la couleur du badge selon le statut
   */
  const getValidationBadge = (status) => {
    switch (status) {
      case VALIDATION_STATUS.DRAFT:
        return <Badge bg="bg-secondary bg-opacity-10 text-secondary border-secondary">Brouillon</Badge>;
      case VALIDATION_STATUS.PENDING:
        return <Badge bg="bg-warning bg-opacity-10 text-warning border-warning bg-warning">En attente</Badge>;
      case VALIDATION_STATUS.VALIDATED:
        return <Badge bg="bg-success bg-opacity-10 text-success border-success">Validé</Badge>;
      case VALIDATION_STATUS.REJECTED:
        return <Badge bg="bg-danger bg-opacity-10 text-danger border-danger">Rejeté</Badge>;
      default:
        return <Badge bg="light" text="dark">Inconnu</Badge>;
    }
  };

  /**
   * VALIDATION DU FORMULAIRE
   */
  useEffect(() => {
    if (!touched) return;

    const errors = [];

    // Validation du montant
    if (!amount) {
      errors.push("Le montant est obligatoire.");
    } else if (isNaN(amount) || Number(amount) <= 0) {
      errors.push("Le montant doit être un nombre supérieur à 0.");
    }

    // Validation du fichier (optionnel mais recommandé)
    if (!proofFile) {
      setFileError("Une preuve de dépôt est recommandée pour tracer l'opération.");
    } else {
      setFileError("");
    }

    setError(errors.length > 0 ? errors.join(" ") : "");
  }, [amount, proofFile, touched]);

  /**
   * GESTIONNAIRE DE SOUMISSION DU FORMULAIRE
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // VÉRIFICATION DU STATUT DE VALIDATION DU COMPTE
    if (!isAccountValidated) {
      toast.error(
        <div>
          <strong>Compte non validé</strong>
          <div className="small mt-1">
            Le compte doit être validé avant de pouvoir ajouter du cash.
            <br />
            Statut actuel : {getValidationStatusText(account.validationStatus)}
          </div>
        </div>,
        { duration: 5000 }
      );
      return;
    }

    // Activation de la validation
    setTouched(true);

    // Vérification des erreurs bloquantes
    if (error || !amount || isNaN(amount) || Number(amount) <= 0) {
      toast.error("Veuillez corriger les erreurs avant de soumettre.");
      return;
    }

    setLoading(true);

    try {
      // Appel API avec le fichier de preuve
      await updateAccountCash(account.id, amount, proofFile);
      
      // Succès
      toast.success(
        <div>
          <strong>Cash ajouté avec succès !</strong>
          <div className="small mt-1">
            Montant : {Number(amount).toLocaleString()} 
            {proofFile && " • Preuve enregistrée"}
          </div>
        </div>,
        { duration: 4000 }
      );
      onSuccess?.();
      
      // Réinitialisation et fermeture
      resetForm();
      onClose();
      
    } catch (err) {
      // Gestion des erreurs
      console.error("Erreur lors de l'ajout du cash:", err);
      
      // Amélioration des messages d'erreur
      let userMessage = err.message || "Erreur lors de l'ajout du cash.";
      
      if (err.message?.includes("non validé") || err.message?.includes("not validated")) {
        userMessage = "Le compte n'est pas validé. Veuillez valider le compte avant d'ajouter du cash.";
      } else if (err.message?.includes("401") || err.message?.includes("non autorisé")) {
        userMessage = "Session expirée. Veuillez vous reconnecter.";
      } else if (err.message?.includes("403") || err.message?.includes("interdit")) {
        userMessage = "Accès refusé. Vous n'avez pas les permissions nécessaires.";
      }
      
      toast.error(
        <div>
          <strong>Erreur</strong>
          <div className="small mt-1">{userMessage}</div>
        </div>,
        { duration: 5000 }
      );
    } finally {
      // Nettoyage
      setLoading(false);
    }
  };

  /**
   * GESTIONNAIRE DE SÉLECTION DE FICHIER
   */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validation basique du fichier
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (file.size > maxSize) {
        setFileError("Le fichier est trop volumineux (max 10MB)");
        setProofFile(null);
        return;
      }

      // Validation du type de fichier
      const allowedTypes = [
        'image/jpeg', 
        'image/png', 
        'image/jpg', 
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setFileError("Type de fichier non autorisé. Formats acceptés: JPG, PNG, PDF, DOC, DOCX");
        setProofFile(null);
        return;
      }

      setProofFile(file);
      setFileError("");
    } else {
      setProofFile(null);
      setFileError("");
    }
  };

  /**
   * RÉINITIALISATION DU FORMULAIRE
   */
  const resetForm = () => {
    setAmount("");
    setProofFile(null);
    setError("");
    setFileError("");
    setTouched(false);
  };

  /**
   * GESTIONNAIRE DE FERMETURE
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * AFFICHAGE DU NOM DU FICHIER SÉLECTIONNÉ
   */
  const getFileName = () => {
    return proofFile ? proofFile.name : "Aucun fichier sélectionné";
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-money-bill-wave me-2"></i>
          Ajouter du cash
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* BANNIÈRE D'INFORMATION DU COMPTE */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              <i className="fas fa-user me-2"></i>
              Compte : {account.displayName || account.companyName || `${account.firstName} ${account.lastName}`}
            </h6>
            <div>
              {getValidationBadge(account.validationStatus)}
            </div>
          </div>
          
          {/* MESSAGE D'ERREUR SI COMPTE NON VALIDÉ */}
          {!isAccountValidated && (
            <Alert variant="danger" className="py-2">
              <div className="d-flex align-items-center">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <div>
                  <strong>Compte non validé</strong>
                  <p className="mb-0 small">
                    {validationError}
                    <br />
                    <small className="text-muted">
                      Vous devez soumettre ce compte à validation avant de pouvoir ajouter du cash.
                    </small>
                  </p>
                </div>
              </div>
            </Alert>
          )}
        </div>

        <Form onSubmit={handleSubmit}>
          {/* CHAMP MONTANT */}
          <Form.Group className="mb-3">
            <Form.Label>
              <i className="fas fa-coins me-2"></i>
              Montant à ajouter <span className="text-danger">*</span>
            </Form.Label>
            <div className="input-group">
              <span className="input-group-text">XAF</span>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                placeholder="Entrez le montant à ajouter"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={() => setTouched(true)}
                isInvalid={touched && !!error}
                disabled={loading || !isAccountValidated}
                className={!isAccountValidated ? "bg-light" : ""}
              />
            </div>
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Le montant doit être supérieur à 0.
            </Form.Text>
          </Form.Group>

          {/* CHAMP FICHIER DE PREUVE */}
          <Form.Group className="mb-4">
            <Form.Label>
              <i className="fas fa-file-upload me-2"></i>
              Preuve de dépôt <span className="text-warning">*</span>
              <small className="text-muted ms-1">(Recommandé)</small>
            </Form.Label>
            
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              disabled={loading || !isAccountValidated}
              className={!isAccountValidated ? "bg-light" : ""}
            />
            
            {/* Affichage du nom du fichier */}
            {proofFile && (
              <div className="mt-2">
                <small className="text-success">
                  <i className="fas fa-file-earmark-check me-1"></i> 
                  Fichier sélectionné: {getFileName()}
                  <span className="text-muted ms-2">
                    ({Math.round(proofFile.size / 1024)} KB)
                  </span>
                </small>
              </div>
            )}
            
            {/* Message d'erreur fichier */}
            {fileError && (
              <Form.Text className="text-warning d-block mt-1">
                <i className="fas fa-exclamation-triangle me-1"></i> 
                {fileError}
              </Form.Text>
            )}
            
            <Form.Text className="text-muted">
              Formats acceptés: JPG, PNG, PDF, DOC, DOCX (max 10MB)
            </Form.Text>
          </Form.Group>

          {/* ALERTE INFORMATIVE */}
          <Alert variant="info" className="small">
            <i className="fas fa-info-circle me-2"></i> 
            <strong> Information importante :</strong> 
            <ul className="mb-0 mt-1">
              <li>L'ajout de cash sera tracé dans l'historique du compte.</li>
              <li>Une preuve de dépôt est recommandée pour la traçabilité.</li>
              <li>Le compte doit être validé pour cette opération.</li>
            </ul>
          </Alert>

          {/* BOUTONS D'ACTION */}
          <div className="d-flex justify-content-end gap-2">
            <Button 
              variant="outline-secondary" 
              onClick={handleClose}
              disabled={loading}
            >
              <i className="fas fa-times me-2"></i>
              Annuler
            </Button>
            
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading || !!error || !isAccountValidated}
              className={!isAccountValidated ? "disabled" : ""}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Ajout en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-2"></i>
                  Ajouter le cash
                </>
              )}
            </Button>
          </div>
          
          {/* MESSAGE SI BOUTON DÉSACTIVÉ */}
          {!isAccountValidated && (
            <div className="mt-3 text-center">
              <small className="text-danger">
                <i className="fas fa-lock me-1"></i>
                L'ajout de cash est désactivé car le compte n'est pas validé. Veillez patientez ou contactez le validateur pour une validation
              </small>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CashEditForm;